import { createContext, ReactNode, useState } from 'react';

import { Volume } from '~/shared/types/api';

export interface DatafySwitchVolume {
  volumeId: Volume['volumeId'];
  state: 'datafying' | 'undatafying';
  timeStamp: Date;
}

export type DatafySwitchContextType = {
  datafySwitchVolumes: DatafySwitchVolume[];
  addDatafySwitchVolume: (
    volumeId: DatafySwitchVolume['volumeId'],
    state: DatafySwitchVolume['state']
  ) => void;
  toggleDatafySwitchVolumeState: (volumeId: DatafySwitchVolume['volumeId']) => void;
  removeDatafySwitchVolume: (volumeId: DatafySwitchVolume['volumeId']) => void;
};

export const DatafySwitchContext = createContext<DatafySwitchContextType>(null!);

const DatafySwitchProvider = ({ children }: { children: ReactNode }) => {
  const [datafySwitchVolumes, setDatafySwitchVolumes] = useState<DatafySwitchVolume[]>([]);

  function addDatafySwitchVolume(
    volumeId: DatafySwitchVolume['volumeId'],
    state: DatafySwitchVolume['state']
  ) {
    setDatafySwitchVolumes((prev) => [...prev, { volumeId, state, timeStamp: new Date() }]);
  }

  function toggleDatafySwitchVolumeState(volumeId: DatafySwitchVolume['volumeId']) {
    const switchVolumes = [...datafySwitchVolumes];
    const volumeIndex = datafySwitchVolumes.findIndex((v) => v.volumeId === volumeId);

    if (volumeIndex === -1) {
      return;
    }

    const state: DatafySwitchVolume['state'] =
      datafySwitchVolumes[volumeIndex]?.state === 'datafying' ? 'undatafying' : 'datafying';

    switchVolumes[volumeIndex] = {
      volumeId,
      state,
      timeStamp: new Date(),
    };
    setDatafySwitchVolumes(switchVolumes);
  }

  function removeDatafySwitchVolume(volumeId: DatafySwitchVolume['volumeId']) {
    setDatafySwitchVolumes((prev) => prev.filter((volume) => volume.volumeId !== volumeId));
  }

  return (
    <DatafySwitchContext.Provider
      value={{
        datafySwitchVolumes,
        addDatafySwitchVolume,
        toggleDatafySwitchVolumeState,
        removeDatafySwitchVolume,
      }}
    >
      {children}
    </DatafySwitchContext.Provider>
  );
};

export default DatafySwitchProvider;
